import { useCallback } from "react";
import classNames from "classnames";
import { Input as BaseInput } from "@base-ui/react/input";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import styles from "./Input.module.scss";
import { getInputRootClassName } from "./inputRecipe";
import type {
    InputControlProps,
    InputRootProps,
    InputSlotProps,
} from "./Input.types";

/** Chrome only: border, background, radius, focus ring. Owns no input logic. */
export const InputRoot = ({
    variant = "primary",
    size,
    fontSize = 14,
    disabled = false,
    className,
    children,
    ref,
    ...props
}: InputRootProps) => {
    const resolvedSize = size ?? "md";

    return (
        <div
            ref={ref}
            className={getInputRootClassName({
                variant,
                size: resolvedSize,
                fontSize,
                className,
            })}
            data-variant={variant}
            data-size={resolvedSize}
            data-disabled={disabled ? "" : undefined}
            {...props}
        >
            {children}
        </div>
    );
};

/**
 * The <input>, rendered through Base UI so a future Field.Root wires
 * aria-*, name registration and validity onto it. Base UI types its ref as
 * HTMLElement; the callback narrows so consumers get HTMLInputElement.
 */
export const InputControl = ({
    className,
    ref,
    ...props
}: InputControlProps) => {
    const assignRef = useCallback(
        (node: HTMLElement | null) => {
            const input = node instanceof HTMLInputElement ? node : null;
            mergeRefs([ref])(input);
        },
        [ref],
    );

    return (
        <BaseInput
            ref={assignRef}
            className={classNames(styles.control, className)}
            {...props}
        />
    );
};

/** Adornment box. A child marked data-slot-box triggers the padding trim. */
export const InputSlot = ({ side, className, children }: InputSlotProps) => (
    <span className={classNames(styles.slot, className)} data-side={side}>
        {children}
    </span>
);
